// GenericFormModal.tsx
import React, { useEffect, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useFormik } from 'formik';
import {
  Dialog,
  DialogContent,
  Grid,
  Box,
  Typography,
  IconButton,
  SxProps,
  Divider
} from '@mui/material';
import { Button } from 'system/atoms';
import * as Yup from 'yup';
import { formModalAtom } from 'services/modals/atoms';
import { Input } from 'components/Inputs';
import CloseIcon from '@mui/icons-material/Close';
import { capitalizeFirstLetter } from 'utils/fomatting';

const headerContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between'
};

const bodyContainer: SxProps = {
  marginTop: 4
};

const FormModalComponent: React.FC = () => {
  const state = useRecoilValue(formModalAtom);
  const setModalState = useSetRecoilState(formModalAtom);

  const { isOpen, fields, onSubmit, title, subtitle, submitText } = state;

  const { initialValues, validationSchema } = useMemo(() => {
    return fields.reduce(
      (acc, field) => {
        return {
          ...acc,
          validationSchema: {
            ...acc.validationSchema,
            [field.id]: field.isRequired
              ? Yup.string().required(
                  field?.requiredErrorText || `${field.label} is required`
                )
              : field.validation
          },
          initialValues: {
            ...acc.initialValues,
            [field.id]: field.initialValue
          }
        };
      },
      {
        validationSchema: {},
        initialValues: {}
      }
    );
  }, [fields]);

  const formik = useFormik<any>({
    initialValues,
    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values) => {
      try {
        if (onSubmit) {
          await onSubmit(values);
        }
      } finally {
        formik.setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (isOpen === false) {
      formik.setSubmitting(false);
      formik.resetForm();
    }
  }, [isOpen]);

  const onClose = () => {
    setModalState({
      fields: [],
      title: '',
      isOpen: false
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogContent sx={{ borderRadius: 0 }}>
        <Box sx={headerContainer}>
          <Box>
            <Typography fontWeight="bold" variant="h6">
              {title}
            </Typography>

            <Typography variant="subtitle1">{subtitle}</Typography>
          </Box>
          <Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={bodyContainer}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {fields.map((field) => (
                <Grid item xs={12} key={field.id}>
                  <Input
                    id={field.id}
                    name={field.id}
                    label={field.label}
                    value={formik.values[field.id]}
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[field.id] &&
                      Boolean(formik.errors[field.id])
                    }
                    helperText={
                      formik.touched[field.id] && formik.errors[field.id]
                        ? capitalizeFirstLetter(
                            formik.errors[field.id] as string
                          )
                        : undefined
                    }
                    {...field.props}
                  />
                </Grid>
              ))}
              <Divider sx={{ my: 4, width: '100vw' }} />
              <Grid item xs={12} sx={{ display: 'flex' }}>
                <Button onClick={onClose} variant="tertiary" fullWidth>
                  Cancel
                </Button>
                <Box width={16} />
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={formik.isSubmitting}
                >
                  {(() => {
                    if (formik.isSubmitting) {
                      return 'Loading...';
                    }

                    if (submitText) {
                      return submitText;
                    }

                    return 'Submit';
                  })()}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const FormModal = () => {
  const { isOpen } = useRecoilValue(formModalAtom);

  if (isOpen) return <FormModalComponent />;

  return null;
};

export { FormModal };
